import { createContext, useContext } from 'react'
import { useSelector } from '@xstate/react'
import type { ActorRefFrom, SnapshotFrom } from 'xstate'
import { emergencyMachine } from './machine'

// Types for the actor
type EmergencyActor = ActorRefFrom<typeof emergencyMachine>
type EmergencySnapshot = SnapshotFrom<typeof emergencyMachine>

// Context for the emergency actor
const EmergencyActorContext = createContext<EmergencyActor | null>(null)

/**
 * Provider component - wraps the flow and provides the actor to children
 */
export function EmergencyActorProvider({
  actor,
  children
}: {
  actor: EmergencyActor
  children: React.ReactNode
}) {
  return <EmergencyActorContext.Provider value={actor}>{children}</EmergencyActorContext.Provider>
}

/**
 * Hook to get the emergency actor for sending events
 */
function useEmergencyActor(): EmergencyActor {
  const actor = useContext(EmergencyActorContext)
  if (!actor) {
    throw new Error('useEmergencyActor must be used within EmergencyActorProvider')
  }
  return actor
}

/**
 * Hook to send events to the emergency machine
 */
export function useEmergencySend() {
  const actor = useEmergencyActor()
  return actor.send
}

/**
 * Hook to select specific parts of the emergency state
 * Only re-renders when the selected value changes
 */
export function useEmergencySelector<T>(selector: (snapshot: EmergencySnapshot) => T): T {
  const actor = useEmergencyActor()
  return useSelector(actor, selector)
}
